import { Alert, Box, Stack, CircularProgress, Grid, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { PortfolioOverview } from './PortfolioOverview';
import { PortfolioAssets } from './PortfolioAssets';
import { PortfolioPositions } from './PortfolioPositions';
import { PortfolioSettlements } from './PortfolioSettlements';
import { PortfolioTrades } from './PortfolioTrades';
import { PortfolioTransfers } from './PortfolioTransfers';
import { AccountInfo, AccountPortfolio, AccountTrade, AccountTransfer, Position, Settlement } from "../../types";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDataProvider, useGetIdentity } from "react-admin";

// Add this to your Portfolio.tsx imports
import { PDFDownloadLink, Document, Page, Text } from '@react-pdf/renderer';
import { AccountPDFDocument } from './components';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useRealtimeStream } from '../../hooks/useRealtimeStream';
import { useHighlightedIds } from '../../hooks/useHighlightedIds';
import { mergeRtdbEntries } from '../../hooks/useMergedRtdbEntries';
import { resolveValuation } from '../../utils/resolveValuation';
import { useAccountPortfolios } from '../../hooks/useAccountPortfolios';
import { useAccountTrades } from '../../hooks/useAccountTrades';
import { useAccountTransfers } from '../../hooks/useAccountTransfers';
import { useCombinedPortfolios } from '../../hooks/useCombinedPortfolios';
import { useCombinedTrades } from '../../hooks/useCombinedTrades';
import { useCombinedTransfers } from '../../hooks/useCombinedTransfers';
import { getDefaultInterval, INTERVALS, Interval } from './intervalDefaults';
import { startOfToday } from 'date-fns';

const ALL_ACCOUNTS = '__all__';

const ACCOUNT_DISPLAY_NAMES: Record<string, string> = {
    'binance:trading-binance_sub14@newgate.de': 'Track_Record_1',
};

// Composite accounts: when selected, also load trades/transfers from these additional accounts
const COMPOSITE_ACCOUNTS: Record<string, string[]> = {
    'binance:trading-binance_sub14@newgate.de': [
        'ethereum:0x59347361AE8DFA71837B4a69b75Fd771CF77733B',
    ],
};

const getAccountLabel = (account: string) => ACCOUNT_DISPLAY_NAMES[account] ?? account;

const DEFAULT_ACCOUNT = 'binance:trading-binance_sub14@newgate.de';
const DEFAULT_ACCOUNT_START = new Date(2023, 8, 1); // 01.09.2023

function safeISODate(date: Date): string {
    return isNaN(date.getTime()) ? 'invalid' : date.toISOString().split('T')[0];
}

export const PortfolioView = () => {
    // base currency selection
    const [currency, setCurrency] = useState<'eur' | 'chf' | 'usd'>('eur');

    // Account selection state
    const [availableAccounts, setAvailableAccounts] = useState<string[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
    const isCombined = selectedAccount === ALL_ACCOUNTS;
    const [positions, setPositions] = useState<Position[]>([]);
    const [settlements, setSettlements] = useState<Settlement[]>([]);
    const [funds, setFunds] = useState<Record<string, string>>({});

    // Date range state — committed values drive Cloud Function hooks
    const initialFrom = new Date(2020, 0, 1);
    const initialTo = new Date();
    const [fromDate, setFromDate] = useState<Date>(initialFrom);
    const [selectedDateRange, setSelectedDateRange] = useState<number | string | null>(null);
    const [toDate, setToDate] = useState<Date>(initialTo);
    const [interval, setInterval_] = useState<Interval>(() => getDefaultInterval(initialFrom, initialTo));
    const [isIntervalManual, setIsIntervalManual] = useState(false);

    // Draft state for DatePickers — updated on every keystroke, committed on blur/Enter/calendar
    const [draftFrom, setDraftFrom] = useState<Date | null>(fromDate);
    const [draftTo, setDraftTo] = useState<Date | null>(toDate);

    const commitFrom = (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
            setFromDate(value);
            setDraftFrom(value);
            if (!isIntervalManual) setInterval_(getDefaultInterval(value, toDate));
        }
    };
    const commitTo = (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
            setToDate(value);
            setDraftTo(value);
            if (!isIntervalManual) setInterval_(getDefaultInterval(fromDate, value));
        }
    };

    const dataProvider = useDataProvider();
    const { identity, isLoading: identityLoading } = useGetIdentity();

    const singleAccountId = isCombined ? null : selectedAccount;
    const compositeIds = selectedAccount ? COMPOSITE_ACCOUNTS[selectedAccount] ?? [] : [];

    const {
        data: portfolios,
        loading: portfoliosLoading,
    } = useAccountPortfolios(singleAccountId, fromDate, toDate, interval);

    const {
        data: trades,
        loading: tradesLoading,
    } = useAccountTrades(singleAccountId, fromDate, toDate);

    const {
        data: transfers,
        loading: transfersLoading,
    } = useAccountTransfers(singleAccountId, fromDate, toDate);

    // Composite account: load trades/transfers from linked accounts
    const compositeAccountId = compositeIds.length > 0 ? compositeIds[0] : null;
    const {
        data: compositeTrades,
        loading: compositeTradesLoading,
    } = useAccountTrades(compositeAccountId, fromDate, toDate);

    const {
        data: compositeTransfers,
        loading: compositeTransfersLoading,
    } = useAccountTransfers(compositeAccountId, fromDate, toDate);

    const {
        data: combinedPortfolios,
        perAccount: perAccountSnapshots,
        loading: combinedPortfoliosLoading,
        error: combinedPortfoliosError,
    } = useCombinedPortfolios(
        isCombined ? availableAccounts : [],
        fromDate, toDate, interval,
    );

    const {
        data: combinedTrades,
        loading: combinedTradesLoading,
        error: combinedTradesError,
    } = useCombinedTrades(isCombined ? availableAccounts : [], fromDate, toDate);

    const {
        data: combinedTransfers,
        loading: combinedTransfersLoading,
        error: combinedTransfersError,
    } = useCombinedTransfers(isCombined ? availableAccounts : [], fromDate, toDate);

    // Merge composite account data into primary account data
    const allTrades = useMemo(() => {
        if (compositeTrades.length === 0) return trades;
        return [...trades, ...compositeTrades].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }, [trades, compositeTrades]);

    const allTransfers = useMemo(() => {
        if (compositeTransfers.length === 0) return transfers;
        return [...transfers, ...compositeTransfers].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }, [transfers, compositeTransfers]);

    const loading = isCombined
        ? combinedPortfoliosLoading || combinedTradesLoading || combinedTransfersLoading
        : portfoliosLoading || tradesLoading || transfersLoading || compositeTradesLoading || compositeTransfersLoading;

    const combinedError = isCombined
        ? combinedPortfoliosError || combinedTradesError || combinedTransfersError
        : null;

    const isLive = toDate >= startOfToday();

    // Real-time RTDB streaming
    const rtdbUid = isLive ? identity?.id?.toString() : undefined;
    const { data: livePortfolios, connected: rtdbConnected } = useRealtimeStream<AccountPortfolio>('portfolios', rtdbUid);
    const { data: liveTrades } = useRealtimeStream<AccountTrade>('account_trades', rtdbUid);
    const { data: liveTransfers } = useRealtimeStream<AccountTransfer>('transfers', rtdbUid);

    // Trigger reference for AnimatedValue — only changes when RTDB data updates,
    // NOT when the user changes date range, currency, or account.
    const liveTrigger = useMemo(
        () => ({ p: livePortfolios, t: liveTrades, x: liveTransfers }),
        [livePortfolios, liveTrades, liveTransfers]
    );

    // Detect if any displayed portfolio entry lacks valuation for selected currency
    // Checks RTDB live data — but since this depends on mergedPortfolios which
    // is computed later, we also add a second check below (hasPartialDisplayData)
    const hasPartialRtdbData = useMemo(() => {
        if (!isLive || !rtdbConnected || !livePortfolios) return false;
        return Object.values(livePortfolios).some(
            (p) => resolveValuation(p.valuation, currency) == null
        );
    }, [livePortfolios, rtdbConnected, isLive, currency]);

    const now = new Date();
    const lastYear = now.getFullYear() - 1;
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthCode = lastMonth.toLocaleDateString('en', { month: 'short' });

    const dateRangeButtons = [
        { label: lastYear.toString(), days: 'last_year' },
        { label: lastMonthCode, days: 'last_month' },
        { label: 'All', days: null },
        { label: 'YTD', days: 'ytd' },
    ];

    const accountInfo: AccountInfo = useMemo(() => ({
        name: "Newgate GmbH",
        account: "",
        accountType: "Company",
    }), []);

    const handleDateRangeClick = (days: number | string | null) => {
        setSelectedDateRange(days);

        const now = new Date();
        let from: Date;
        let to: Date = now;

        if (days === null) {
            from = new Date(2020, 0, 1);
        } else if (days === 'ytd') {
            from = new Date(now.getFullYear(), 0, 1);
        } else if (days === 'last_year') {
            from = new Date(now.getFullYear() - 1, 0, 1);
            to = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        } else if (days === 'last_month') {
            from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        } else if (typeof days === 'number') {
            from = new Date();
            from.setDate(now.getDate() - days);
        } else {
            return;
        }

        setFromDate(from);
        setToDate(to);
        setDraftFrom(from);
        setDraftTo(to);
        setIsIntervalManual(false);
        setInterval_(getDefaultInterval(from, to));
    };


    // Fetch available accounts
    useEffect(() => {
        if (!identity || !identity.id) return;

        const fetchAccounts = async () => {
            try {
                const { data: userData } = await dataProvider.getOne('users', { id: identity.id.toString() });

                const entityIds: string[] = userData.entity_ids || [];
                if (entityIds.length === 0) {
                    setAvailableAccounts([]);
                    return;
                }

                // Resolve account_ids from each entity, tolerating missing entity docs
                const entityResults = await Promise.all(
                    entityIds.map(async (eid) => {
                        try {
                            const { data: entityData } = await dataProvider.getOne('entities', { id: eid });
                            return (entityData.account_ids as string[]) || [];
                        } catch {
                            return [];
                        }
                    })
                );

                // Union all account_ids across entities (deduplicate)
                const accountIds = [...new Set(entityResults.flat())];

                if (accountIds.length > 0) {
                    setAvailableAccounts(accountIds);
                    const stored = localStorage.getItem('selectedAccount');
                    let chosen: string;
                    if (stored === ALL_ACCOUNTS && accountIds.length > 1) {
                        chosen = ALL_ACCOUNTS;
                    } else if (stored && accountIds.includes(stored)) {
                        chosen = stored;
                    } else if (accountIds.includes(DEFAULT_ACCOUNT)) {
                        chosen = DEFAULT_ACCOUNT;
                    } else {
                        chosen = accountIds[0];
                    }
                    setSelectedAccount(chosen);

                    // Apply custom start date for the default account
                    if (chosen === DEFAULT_ACCOUNT) {
                        setFromDate(DEFAULT_ACCOUNT_START);
                        setDraftFrom(DEFAULT_ACCOUNT_START);
                        setInterval_(getDefaultInterval(DEFAULT_ACCOUNT_START, toDate));
                    }
                }
            } catch (error) {
                console.error("❌ Error fetching accounts:", error);
            }
        };

        fetchAccounts();
    }, [identity, dataProvider]);

    // Merge RTDB live data with Firestore historical data
    // Note: RTDB keys are sanitized (e.g. ":" → "_") and unsanitizeKey only reverses
    // +, @, . — so we match by the account_id field inside the data, not by key.
    const mergedPortfolios = useMemo(() => {
        if (!livePortfolios || !selectedAccount) return portfolios;
        const live = Object.values(livePortfolios).find(
            (p) => p.account_id === selectedAccount
        );
        if (!live) return portfolios;
        // RTDB returns timestamps as ISO strings, not Date objects
        const liveTs = new Date(live.timestamp as unknown as string);
        if (portfolios.length > 0 && liveTs <= portfolios[portfolios.length - 1].timestamp) {
            return portfolios;
        }
        return [...portfolios, { ...live, timestamp: liveTs }];
    }, [portfolios, livePortfolios, selectedAccount]);

    const mergedTrades = useMemo(
        () => mergeRtdbEntries(allTrades, liveTrades, selectedAccount),
        [allTrades, liveTrades, selectedAccount]
    );

    const mergedTransfers = useMemo(
        () => mergeRtdbEntries(allTransfers, liveTransfers, selectedAccount),
        [allTransfers, liveTransfers, selectedAccount]
    );

    // Stable key extractors for useHighlightedIds
    const getTradeKey = useCallback((t: AccountTrade) => t.transaction_id, []);
    const getTransferKey = useCallback((t: AccountTransfer) => t.transaction_id, []);

    // Detect newly-added trades/transfers for row highlighting (RTDB live entries only)
    const highlightedTradeIds = useHighlightedIds(mergedTrades, getTradeKey);
    const highlightedTransferIds = useHighlightedIds(mergedTransfers, getTransferKey);

    // Use Firestore-only data for PDF to avoid re-generating the PDF on every RTDB update
    const pdfData = useMemo(() => {
        if (loading || !selectedAccount) return null;
        return {
            portfolio: portfolios[portfolios.length - 1] || null,
            firstPortfolio: portfolios[0] || null,
            trades: allTrades,
            transfers: allTransfers,
            currency,
            accountId: selectedAccount,
            accountInfo,
            dateRange: { from: fromDate, to: toDate }
        };
    }, [loading, selectedAccount, portfolios, allTrades, allTransfers, currency, fromDate, toDate, accountInfo]);

    // get latest portfolio snapshot
    const latestPortfolio = mergedPortfolios.length > 0
        ? mergedPortfolios[mergedPortfolios.length - 1]
        : null;

    const resolvedPortfolios = isCombined ? combinedPortfolios : mergedPortfolios;
    const resolvedTrades = isCombined ? combinedTrades : mergedTrades;
    const resolvedTransfers = isCombined ? combinedTransfers : mergedTransfers;
    const resolvedLatestPortfolio = resolvedPortfolios.length > 0
        ? resolvedPortfolios[resolvedPortfolios.length - 1]
        : null;

    // Check if the latest displayed portfolio lacks valuation for the selected currency
    const hasPartialData = useMemo(() => {
        if (hasPartialRtdbData) return true;
        if (!isLive || !rtdbConnected) return false;
        if (resolvedLatestPortfolio && resolveValuation(resolvedLatestPortfolio.valuation, currency) == null) return true;
        return false;
    }, [hasPartialRtdbData, isLive, rtdbConnected, resolvedLatestPortfolio, currency]);

    // Compute total portfolio value in EUR
    const total_portfolio_value = positions.reduce((sum, position) => {
        return sum + (position.value?.global?.EUR || 0);
    }, 0);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Stack spacing={2} mt={2}>
                {/* Controls Grid */}
                <Box>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                            <FormControl fullWidth>
                                <InputLabel id="account-select-label">Account</InputLabel>
                                <Select
                                    labelId="account-select-label"
                                    value={selectedAccount || ""}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === ALL_ACCOUNTS) {
                                            setSelectedAccount(ALL_ACCOUNTS);
                                            localStorage.setItem('selectedAccount', ALL_ACCOUNTS);
                                        } else {
                                            const selected = availableAccounts.find(acc => acc === val);
                                            if (selected) {
                                                setSelectedAccount(selected);
                                                localStorage.setItem('selectedAccount', selected);
                                                if (selected === DEFAULT_ACCOUNT) {
                                                    setFromDate(DEFAULT_ACCOUNT_START);
                                                    setDraftFrom(DEFAULT_ACCOUNT_START);
                                                    setInterval_(getDefaultInterval(DEFAULT_ACCOUNT_START, toDate));
                                                }
                                            }
                                        }
                                    }}
                                    label="Account"
                                >
                                    {availableAccounts.length > 1 && (
                                        <MenuItem value={ALL_ACCOUNTS}>
                                            <em>All Accounts</em>
                                        </MenuItem>
                                    )}
                                    {[...availableAccounts]
                                        .sort((a, b) => {
                                            const aRenamed = a in ACCOUNT_DISPLAY_NAMES ? 0 : 1;
                                            const bRenamed = b in ACCOUNT_DISPLAY_NAMES ? 0 : 1;
                                            return aRenamed - bRenamed;
                                        })
                                        .map((account) => (
                                        <MenuItem key={account} value={account}>
                                            {account in ACCOUNT_DISPLAY_NAMES
                                                ? <em>{getAccountLabel(account)}</em>
                                                : getAccountLabel(account)
                                            }
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={2}>
                            <DatePicker
                                label="From"
                                value={draftFrom}
                                onChange={(v) => setDraftFrom(v)}
                                onAccept={(v) => commitFrom(v)}
                                slotProps={{ textField: {
                                    onBlur: () => commitFrom(draftFrom),
                                    onKeyDown: (e) => { if (e.key === 'Enter') commitFrom(draftFrom); },
                                }}}
                            />
                        </Grid>

                        <Grid item xs={6} md={2}>
                            <DatePicker
                                label="To"
                                value={draftTo}
                                onChange={(v) => setDraftTo(v)}
                                onAccept={(v) => commitTo(v)}
                                slotProps={{ textField: {
                                    onBlur: () => commitTo(draftTo),
                                    onKeyDown: (e) => { if (e.key === 'Enter') commitTo(draftTo); },
                                }}}
                            />
                        </Grid>

                        <Grid item xs={12} md={2.5}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {dateRangeButtons.map((range) => (
                                        <Button
                                            key={range.label}
                                            variant="outlined"
                                            size="small"
                                            sx={{ minWidth: 55 }}
                                            onClick={() => handleDateRangeClick(range.days)}
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                                <Box display="flex" gap={1}>
                                    <FormControl
                                        size="small"
                                        sx={{
                                            m: 0,
                                            width: '33%',
                                            '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px' },
                                            '& .MuiOutlinedInput-root': { height: '28px' }
                                        }}
                                    >
                                        <Select
                                            value={interval}
                                            onChange={(e) => {
                                                setInterval_(e.target.value as Interval);
                                                setIsIntervalManual(true);
                                            }}
                                        >
                                            {INTERVALS.map((iv) => (
                                                <MenuItem key={iv} value={iv} sx={{ fontSize: '0.75rem' }}>{iv}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl
                                        size="small"
                                        sx={{
                                            m: 0,
                                            width: '33%',
                                            '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px' },
                                            '& .MuiOutlinedInput-root': { height: '28px' }
                                        }}
                                    >
                                        <Select
                                            value={currency}
                                            onChange={(e) => setCurrency(e.target.value as 'eur' | 'chf' | 'usd')}
                                        >
                                            <MenuItem value="eur" sx={{ fontSize: '0.75rem' }}>EUR</MenuItem>
                                            <MenuItem value="chf" sx={{ fontSize: '0.75rem' }}>CHF</MenuItem>
                                            <MenuItem value="usd" sx={{ fontSize: '0.75rem' }}>USD</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {!isCombined ? (
                                        <PDFDownloadLink
                                            key={`pdf-${selectedAccount}-${currency}-${safeISODate(fromDate)}-${safeISODate(toDate)}`}
                                            document={pdfData ? (
                                                <AccountPDFDocument
                                                    portfolio={pdfData.portfolio}
                                                    firstPortfolio={pdfData.firstPortfolio}
                                                    trades={pdfData.trades}
                                                    transfers={pdfData.transfers}
                                                    currency={pdfData.currency}
                                                    accountId={pdfData.accountId}
                                                    accountInfo={pdfData.accountInfo}
                                                    dateRange={pdfData.dateRange}
                                                />
                                            ) : <Document><Page><Text>Loading...</Text></Page></Document>}
                                            style={{ width: '34%' }}
                                            fileName={`portfolio-statement-${selectedAccount}-${safeISODate(fromDate)}-${safeISODate(toDate)}.pdf`}
                                        >
                                            {({ loading: pdfLoading }) => (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<GetAppIcon />}
                                                    disabled={loading || pdfLoading || !pdfData}
                                                    size="small"
                                                    fullWidth
                                                >
                                                    PDF
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            startIcon={<GetAppIcon />}
                                            disabled
                                            size="small"
                                            sx={{ width: '34%' }}
                                        >
                                            PDF
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Portfolio Components */}
                {combinedError && !loading && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {combinedError.message?.includes('internal') || combinedError.message?.includes('deadline')
                            ? 'The request timed out. Try selecting a shorter date range or a larger interval.'
                            : `Failed to load combined data: ${combinedError.message}`}
                    </Alert>
                )}
                {loading || identityLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="50vh">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <PortfolioOverview
                            portfolios={resolvedPortfolios}
                            trades={resolvedTrades}
                            transfers={resolvedTransfers}
                            fromDate={fromDate}
                            toDate={toDate}
                            currency={currency}
                            liveTrigger={liveTrigger}
                            isCombined={isCombined}
                            accountIds={availableAccounts}
                            compositeAccountIds={compositeIds}
                            isLive={!isCombined && isLive && rtdbConnected}
                            hasPartialData={hasPartialData}
                            partialDataTooltip={`Some accounts don't have valuation data in ${currency.toUpperCase()} yet — those entries are excluded from totals`}
                        />
                        <PortfolioAssets
                            portfolio={resolvedLatestPortfolio}
                            currency={currency}
                            liveTrigger={liveTrigger}
                            perAccountSnapshots={isCombined ? perAccountSnapshots : undefined}
                        />
                        <PortfolioTransfers
                            transfers={resolvedTransfers}
                            currency={currency}
                            account_id={isCombined ? ALL_ACCOUNTS : (resolvedLatestPortfolio?.account_id || "")}
                            from={fromDate}
                            to={toDate}
                            highlightedIds={isCombined ? undefined : highlightedTransferIds}
                            isCombined={isCombined}
                            accountIds={isCombined ? availableAccounts : undefined}
                        />
                        <PortfolioTrades
                            trades={resolvedTrades}
                            currency={currency}
                            account_id={isCombined ? ALL_ACCOUNTS : (resolvedLatestPortfolio?.account_id || "")}
                            from={fromDate}
                            to={toDate}
                            highlightedIds={isCombined ? undefined : highlightedTradeIds}
                            isCombined={isCombined}
                        />
                        <PortfolioPositions positions={positions} total_portfolio_value={total_portfolio_value} funds={funds} />
                        <PortfolioSettlements settlements={settlements} funds={funds} />
                        <Box sx={{ height: 16 }} />
                    </>
                )}
            </Stack>
        </LocalizationProvider >
    );
};